import React, { useState } from 'react';
import styled from 'styled-components';
import { useParams, Outlet } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { Typography } from 'design-system/src/components/Typography/Typography';
import EyeIcon from 'design-system/src/assets/icons/eye.svg?react';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import FilePreviewModal from 'design-system/src/components/FilePreviewModal/FilePreviewModal';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';
import { DownloadFileButton } from 'design-system/src/components/DownloadFileButton/DownloadFileButton';
import { IconButton } from 'design-system/src/components/Button/Button';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import PageContainer from '../components/PageContainer';
import { client, getRxFileDownloadUrl, getRxFilePreviewUrl, TimelineEvent } from '../api';
import { truncateLongText, titleCase } from '../utils';
import type { StatusTimelineCategory } from '../components/rx/types';
import { formatStandardDateLocaleString, formatSimpleDate } from 'shared-utils/utils/formatDate';

import type { RxDocument, RxResponse, LastContactedPatient, FileType, RxTimelineStatus, Rx } from '../api';
import { ItemViewTopNav } from '../components/tableItemDetailViewComponents/ItemViewTopNav';
import {
  BodyContainer,
  TopNavAndContent,
  ContentOuterWrapper,
  CenteredContent,
} from '../components/tableItemDetailViewComponents/Containers';
import { Sidebar, SidebarTopBar } from 'design-system/src/components/Sidebar/Sidebar';
import {
  DrugPanel,
  PatientPanel,
  PrescriberPanel,
  PharmacyPanel,
  InsurancePanel,
} from '../components/sidebar/SidebarPanels';
import { GeneralInfoTable } from 'design-system/src/components/GeneralInfoTable/GeneralInfoTable';
import { CollapsibleTimeline } from '../components/CollapsibleTimeline/CollapsibleTimeline';

import { flexRender, getCoreRowModel, useReactTable, getSortedRowModel } from '@tanstack/react-table';

import { Row, Table, TableCell, TableContainer, TableHeader, HeaderSortingIndicator } from '../components/Table';
import { breakpoints } from 'design-system/src/tokens/breakpoints';
import { createTrackingProps } from 'shared-utils/analytics';
import { ActionItemsAndUpdatesSection } from '../components/rx/ActionItemsAndUpdatesSection.tsx';
import CancelPrescriptionModal from '../components/rx/CancelPrescriptionModal/CancelPrescriptionModal';
import CompletePrescriptionModal from '../components/rx/CompletePrescriptionModal/CompletePrescriptionModal';
import ClosePrescriptionPopoverMenu from '../components/rx/ClosePrescriptionPopoverMenu/ClosePrescriptionPopoverMenu';
import { BenefitsVerificationResults } from '../components/rx/BenefitsVerificationResults';

const HeadlineContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

const DownloadFileCell = styled.div`
  display: flex;
  gap: 4px;
  color: var(--grayscale-600);
  // only show the preview button on large screens
  > button {
    @media (max-width: ${breakpoints.tabletMax}px) {
      display: none;
    }
  }
`;

const TimelinesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

interface PrescriptionHistorySection {
  category: StatusTimelineCategory;
  status: RxTimelineStatus;
  status_detail: string;
  timeline: TimelineEvent[];
}

/**
 * Transforms Rx status_timelines into an array of PrescriptionHistorySection objects
 * Also filters out any enrollment timelines that do not have statuses or events
 * @param statusTimelines - The status_timelines object from an Rx
 * @returns Array of PrescriptionHistorySection objects, filtered to exclude undefined entries
 */
const processStatusTimelines = (statusTimelines: Rx['status_timelines']): PrescriptionHistorySection[] => {
  return [
    {
      category: 'coverage' as StatusTimelineCategory,
      ...statusTimelines.coverage,
    },
    {
      category: 'pharmacy' as StatusTimelineCategory,
      ...statusTimelines.pharmacy,
    },
    statusTimelines.hub && {
      category: 'hub' as StatusTimelineCategory,
      ...statusTimelines.hub,
    },
    statusTimelines.bridge && {
      category: 'bridge' as StatusTimelineCategory,
      ...statusTimelines.bridge,
    },
    statusTimelines.pap && {
      category: 'pap' as StatusTimelineCategory,
      ...statusTimelines.pap,
    },
  ].filter((section): section is PrescriptionHistorySection => section !== null);
};

interface PrescriptionHistoryProps {
  sections: PrescriptionHistorySection[];
}

const PrescriptionHistory = ({ sections }: PrescriptionHistoryProps) => {
  return (
    <>
      <Typography
        styledAs="bodyLargeSpaceGrotesk"
        marginBottom="12px"
        weight={500}
        {...createTrackingProps({ unmask: true })}
      >
        Prescription history
      </Typography>

      <TimelinesContainer>
        {sections.map((section) => (
          <CollapsibleTimeline
            key={section.category}
            analyticsName={`Prescription Page: ${titleCase(section.category)} Timeline`}
            category={section.category}
            status={section.status}
            statusDetail={section.status_detail}
            events={section.timeline}
          />
        ))}
      </TimelinesContainer>
    </>
  );
};

const PreviewTableDocumentContainer: React.FC<{
  fileId: string;
  rxId: string;
  documentType: FileType;
}> = ({ fileId, rxId, documentType }) => {
  const [isPreviewVisible, setPreviewVisible] = useState(false);

  const fileUrl = getRxFilePreviewUrl(rxId, fileId);

  const handleFilePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewVisible(true);
  };

  const handleClosePreview = () => {
    setPreviewVisible(false);
  };

  return (
    <Tooltip content="Preview">
      <IconButton onClick={handleFilePreview}>
        <EyeIcon aria-hidden="true" />
        {isPreviewVisible && (
          <FilePreviewModal
            fileUrl={fileUrl}
            onClose={handleClosePreview}
            fileHeader={documentType}
            downloadUrl={getRxFileDownloadUrl(rxId, fileId)}
          />
        )}
      </IconButton>
    </Tooltip>
  );
};

interface DocumentTableProps {
  rxId: string;
  data: RxDocument[];
}

const DocumentsTable = ({ rxId, data }: DocumentTableProps) => {
  const columns = [
    {
      header: 'Date',
      accessorKey: 'date',
      id: 'date',
      // TODO: fix `any`
      cell: (info: any) => formatStandardDateLocaleString(info.getValue()),
    },
    {
      header: 'Document type',
      accessorKey: 'document_type',
      id: 'document_type',
      enableSorting: false,
    },
    {
      header: 'Name',
      accessorKey: 'name',
      id: 'name',
      enableSorting: false,
      cell: (info: any) => truncateLongText(info.getValue()),
    },
    {
      header: '',
      id: 'download',
      enableSorting: false,
      cell: (info: any) => (
        <DownloadFileCell key={info.cell.id}>
          <PreviewTableDocumentContainer
            rxId={rxId}
            fileId={info.row.original.id}
            documentType={info.row.original.document_type}
          />
          <DownloadFileButton downloadUrl={getRxFileDownloadUrl(rxId, info.row.original.id)} />
        </DownloadFileCell>
      ),
    },
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <TableContainer>
      <Table>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} {...createTrackingProps({ unmask: true })}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHeader
                    key={header.id}
                    $sortable={header.column.getCanSort()}
                    onClick={header.column.getCanSort() ? () => header.column.toggleSorting() : undefined}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      <HeaderSortingIndicator
                        isSorted={header.column.getIsSorted()}
                        sortDirection={header.column.getIsSorted()}
                      />
                    )}
                  </TableHeader>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <Row key={row.original.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} $verticalAlign="middle">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </Row>
            ))
          ) : (
            <tr {...createTrackingProps({ unmask: true })}>
              <TableCell colSpan={columns.length}>No documents.</TableCell>
            </tr>
          )}
        </tbody>
      </Table>
    </TableContainer>
  );
};

const RxPage: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isCancelPrescriptionModalOpen, setIsCancelPrescriptionModalOpen] = useState(false);
  const [isCompletePrescriptionModalOpen, setIsCompletePrescriptionModalOpen] = useState(false);

  const params = useParams();
  const query = useQuery<RxResponse>({
    queryKey: ['rxs', params.rxId],
    queryFn: () => {
      return client.get(`rxs/${params.rxId}`).then((res) => res.data);
    },
  });

  const queryClient = useQueryClient();

  const getLastContactedPatientCopy = (lastContactPatient: LastContactedPatient) => {
    const { action, timestamp } = lastContactPatient;
    if (!action || !timestamp) {
      return null;
    }
    return `${titleCase(action)} patient on ${formatSimpleDate(timestamp)}`;
  };

  let body;
  if (query.isLoading) {
    body = <LoadingSpinner />;
  } else if (query.error) {
    body = <PageLoadError />;
  } else if (query.data) {
    const { rx, patient, prescriber, drug, pharmacy, insurance, show_update_pharmacy_button, benefits_verification } =
      query.data;
    const { status_timelines, task_list_type, tasks, notifications, documents, last_contacted_patient } = rx;

    const statusTimelines = processStatusTimelines(status_timelines);

    const rxTimeline = (
      <>
        <PrescriptionHistory sections={statusTimelines} />
        <Hairline />
      </>
    );

    const lastContactedPatientCopy = getLastContactedPatientCopy(last_contacted_patient);

    body = (
      <BodyContainer>
        <TopNavAndContent>
          <ItemViewTopNav isSidebarCollapsed={isSidebarCollapsed} setIsSidebarCollapsed={setIsSidebarCollapsed} />
          <ContentOuterWrapper>
            <CenteredContent style={{ containerType: 'inline-size', containerName: 'content-container' }}>
              <HeadlineContainer>
                <Typography styledAs="h6" renderedAs="h1">
                  {drug.name}
                </Typography>
                {!rx.is_canceled && !rx.is_completed && (
                  <ClosePrescriptionPopoverMenu
                    onCompleteClick={() => setIsCompletePrescriptionModalOpen(true)}
                    onCancelClick={() => setIsCancelPrescriptionModalOpen(true)}
                  />
                )}
              </HeadlineContainer>
              <GeneralInfoTable
                patientName={`${patient.first_name} ${patient.last_name}`}
                dob={patient.date_of_birth}
                prescriberName={prescriber.name}
                prescribedOn={drug.prescription_recieved_at}
                // don't pass in the medication info because it's redundant
                prescriptionId={null}
                drugName={null}
                // Hide last contacted row if null
                // TODO: consider showing fallback "Not contacted yet" value instead
                extraData={
                  lastContactedPatientCopy ? [{ label: 'Last contacted', value: lastContactedPatientCopy }] : []
                }
              />

              {rxTimeline}

              {task_list_type !== 'none' && (
                <ActionItemsAndUpdatesSection
                  task_list_type={task_list_type}
                  taskList={tasks}
                  notificationsList={notifications}
                  patient={patient}
                />
              )}
              {benefits_verification && <BenefitsVerificationResults benefitsVerification={benefits_verification} />}
              <Typography
                styledAs="bodyLargeSpaceGrotesk"
                renderedAs="h2"
                weight={500}
                {...createTrackingProps({ unmask: true })}
              >
                Documents
              </Typography>
              <DocumentsTable rxId={params.rxId as string} data={documents} />
            </CenteredContent>
          </ContentOuterWrapper>
        </TopNavAndContent>

        {!isSidebarCollapsed && (
          <Sidebar>
            <SidebarTopBar />
            <DrugPanel drug={drug} linkToRxPage={false} />
            <PharmacyPanel
              pharmacy={pharmacy}
              showUpdatePharmacyButton={show_update_pharmacy_button}
              rxId={drug.id}
              onPharmacyUpdate={() => queryClient.invalidateQueries({ queryKey: ['rxs', params.rxId] })}
            />
            <PatientPanel patient={patient} />
            <PrescriberPanel prescriber={prescriber} />
            <InsurancePanel insurance={insurance} />
          </Sidebar>
        )}
      </BodyContainer>
    );
  }

  return (
    <>
      <PageContainer noPadding={true}>{body}</PageContainer>
      {isCancelPrescriptionModalOpen && query.data && (
        <CancelPrescriptionModal
          closeModal={() => {
            setIsCancelPrescriptionModalOpen(false);
          }}
          rxId={params.rxId as string}
          patient={query.data.patient}
          drug={query.data.drug}
          prescriber={query.data.prescriber}
          prescribedOn={query.data.drug.prescription_recieved_at}
        />
      )}
      {isCompletePrescriptionModalOpen && query.data && (
        <CompletePrescriptionModal
          closeModal={() => {
            setIsCompletePrescriptionModalOpen(false);
          }}
          rxId={params.rxId as string}
          patient={query.data.patient}
          drug={query.data.drug}
          prescriber={query.data.prescriber}
          prescribedOn={query.data.drug.prescription_recieved_at}
        />
      )}
      <Outlet
        context={{
          reload: () => queryClient.resetQueries({ queryKey: ['rxs', params.rxId] }),
        }}
      />
    </>
  );
};

export default RxPage;
